import React, { useState, useRef } from 'react';
import {
  X,
  Upload,
  AlertCircle,
  Loader,
  Eye
} from 'lucide-react';
import { analyticsApi } from '../lib/api';

interface ImportResult {
  imported: number;
  failed: number;
  total: number;
  report_date: string;
  imported_details: Array<{
    row: number;
    ad_name: string;
    ucode: string;
    match_type: string;
    action: string;
    spend: number;
    revenue: number | null;
  }>;
  failed_details: Array<{
    row: number;
    ad_name: string;
    error: string;
  }>;
  summary: {
    message: string;
    total_spend: number;
    total_revenue: number;
  };
}

interface FacebookImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function MetadataImportModal({
  isOpen,
  onClose,
  onSuccess
}: FacebookImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      selectFile(files[0]);
    }
  };

  const selectFile = (selectedFile: File) => {
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
    const validExt = /\.(xlsx|xls|csv)$/i.test(selectedFile.name);

    if (!validTypes.includes(selectedFile.type) && !validExt) {
      alert('Please upload a .xlsx, .xls, or .csv file');
      return;
    }

    setFile(selectedFile);
    setResult(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      selectFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await analyticsApi.importMetadataExcel(formData as any, {
        params: { report_date: reportDate }
      });
      setResult(response.data.data);

      if (response.data.data.imported > 0) {
        // Auto-close after 2 seconds if successful
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setShowPreview(false);
    setReportDate(new Date().toISOString().split('T')[0]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b dark:border-slate-700">
          <h2 className="text-xl font-semibold dark:text-white">Import Facebook Ads Report</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {!result ? (
            <>
              {/* Upload Area */}
              {!file && (
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                    dragActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-slate-600 hover:border-gray-400'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mx-auto mb-3 text-gray-400 dark:text-slate-500" size={32} />
                  <p className="text-lg font-medium dark:text-white mb-1">
                    Drag and drop your Facebook report here
                  </p>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
                    or click to browse (.xlsx, .xls, .csv)
                  </p>
                  <button
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Browse Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>
              )}

              {/* File Selected */}
              {file && !isLoading && (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-900 dark:text-blue-100">{file.name}</p>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => setFile(null)}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Report Date
                    </label>
                    <input
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}

              {/* Helper Text */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm dark:text-blue-100">
                <p className="font-medium text-blue-900 dark:text-blue-300 mb-1">
                  📊 Facebook Report Format:
                </p>
                <ul className="text-blue-800 dark:text-blue-200 space-y-1 ml-4 list-disc text-xs">
                  <li>Ad Name (will auto-match to UCODE)</li>
                  <li>Spend, Impressions, Clicks</li>
                  <li>CTR, CPC, CPM metrics</li>
                  <li>Video plays (25%, 50%, 75%, 100%)</li>
                  <li>Hook Rate, Hold Rate (if available)</li>
                </ul>
                <p className="text-blue-700 dark:text-blue-400 mt-2 text-xs">
                  ✓ System will automatically match ad names to UCODEs and fetch RedTrack revenue data
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleImport}
                  disabled={!file || isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isLoading ? (
                    <>
                      <Loader className="inline animate-spin mr-2" size={18} />
                      Importing...
                    </>
                  ) : (
                    'Import Facebook Report'
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <p className="text-green-600 dark:text-green-400 text-sm font-medium">Imported</p>
                    <p className="text-3xl font-bold text-green-700 dark:text-green-300">
                      {result.imported}
                    </p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                    <p className="text-red-600 dark:text-red-400 text-sm font-medium">Failed</p>
                    <p className="text-3xl font-bold text-red-700 dark:text-red-300">
                      {result.failed}
                    </p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Total</p>
                    <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                      {result.total}
                    </p>
                  </div>
                </div>

                {/* Revenue Summary */}
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-sm dark:text-purple-100">
                  <p className="font-medium text-purple-900 dark:text-purple-300 mb-2">
                    ✓ Report Metrics Imported
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-purple-700 dark:text-purple-400">Total Spend</p>
                      <p className="font-semibold text-purple-900 dark:text-purple-200">
                        ${result.summary.total_spend.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-purple-700 dark:text-purple-400">Total Revenue</p>
                      <p className="font-semibold text-purple-900 dark:text-purple-200">
                        ${result.summary.total_revenue.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Success Details */}
                {result.imported > 0 && (
                  <div>
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-3 font-medium text-sm"
                    >
                      <Eye size={16} />
                      {showPreview ? 'Hide' : 'View'} Imported Details ({result.imported})
                    </button>
                    {showPreview && (
                      <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 max-h-40 overflow-y-auto text-xs">
                        {result.imported_details.map((item, idx) => (
                          <div
                            key={idx}
                            className="py-2 border-b dark:border-slate-600 last:border-b-0"
                          >
                            <p className="font-mono text-blue-600 dark:text-blue-400">
                              {item.ucode}
                            </p>
                            <p className="text-gray-600 dark:text-slate-400">
                              {item.ad_name} • Spend: ${item.spend?.toFixed(2)} • Revenue: ${item.revenue?.toFixed(2) || 'N/A'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Error Details */}
                {result.failed > 0 && (
                  <div>
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="flex items-center gap-2 text-red-600 dark:text-red-400 hover:underline mb-3 font-medium text-sm"
                    >
                      <AlertCircle size={16} />
                      {showPreview ? 'Hide' : 'View'} Errors ({result.failed})
                    </button>
                    {showPreview && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-40 overflow-y-auto text-xs">
                        {result.failed_details.map((item, idx) => (
                          <div
                            key={idx}
                            className="py-2 border-b dark:border-red-700 last:border-b-0"
                          >
                            <p className="font-mono text-red-700 dark:text-red-300">
                              Row {item.row}
                            </p>
                            <p className="text-red-600 dark:text-red-400">{item.ad_name}</p>
                            <p className="text-red-600 dark:text-red-400">{item.error}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer Message */}
              {result.imported > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm dark:text-green-100">
                  <p className="font-medium text-green-900 dark:text-green-300">
                    ✓ Import completed successfully!
                  </p>
                  <p className="text-green-800 dark:text-green-200 text-xs mt-1">
                    Facebook data merged with RedTrack for complete analytics
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t dark:border-slate-700">
          {result ? (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
            >
              Close
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded hover:bg-gray-50 dark:hover:bg-slate-700 font-medium"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
